import {useEffect, useRef, useState} from 'react';
import {isEditableTarget} from './isEditableTarget';

interface UseMenuKeyboardOptions<T> {
    isOpen: boolean;
    items: readonly T[];
    onSelect: (item: T, index: number) => void;
    onClose: () => void;
    enabled?: boolean;
}

export function useMenuKeyboard<T>({
    isOpen,
    items,
    onSelect,
    onClose,
    enabled = true
}: UseMenuKeyboardOptions<T>) {
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const itemsRef = useRef(items);
    const highlightedRef = useRef(0);
    const onSelectRef = useRef(onSelect);
    const onCloseRef = useRef(onClose);

    itemsRef.current = items;
    onSelectRef.current = onSelect;
    onCloseRef.current = onClose;

    useEffect(() => {
        if (isOpen) {
            setHighlightedIndex(0);
            highlightedRef.current = 0;
        }
    }, [isOpen]);

    useEffect(() => {
        highlightedRef.current = highlightedIndex;
    }, [highlightedIndex]);

    useEffect(() => {
        if (!isOpen || !enabled) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            const list = itemsRef.current;

            if (!list.length) {
                return;
            }

            if (event.key !== 'Escape' && isEditableTarget(event.target)) {
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                setHighlightedIndex((index) => {
                    const next = (index + 1) % list.length;
                    highlightedRef.current = next;
                    return next;
                });
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                setHighlightedIndex((index) => {
                    const next = (index - 1 + list.length) % list.length;
                    highlightedRef.current = next;
                    return next;
                });
            } else if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                const index = highlightedRef.current;
                onSelectRef.current(list[index], index);
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onCloseRef.current();
            } else if (event.key === 'Home') {
                event.preventDefault();
                highlightedRef.current = 0;
                setHighlightedIndex(0);
            } else if (event.key === 'End') {
                event.preventDefault();
                const last = list.length - 1;
                highlightedRef.current = last;
                setHighlightedIndex(last);
            }
        };

        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [isOpen, enabled]);

    return {highlightedIndex, setHighlightedIndex};
}
